/**
 * @file useKeptChoices.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-04
 * @description Pricing & Costing's figures SET on a line and not yet
 *              confirmed — an option chosen, or a cost / price typed (with its
 *              note) — kept on the server and shared (Cam, 3 Nov 2026: "a set
 *              cost or price can sometimes appear to just vanish and need
 *              setting again!"; 4 Nov: re-read every 20 seconds, never
 *              overwritten silently, every set in the history).
 *
 *   Why it vanished: the screen kept these only in the tab's sessionStorage
 *   (usePersistedState 'pricingCosting.choices'), so they were gone after
 *   signing out (App.js clears every page's slot), in another tab or on
 *   another PC, when the browser was closed, and nobody else ever saw them.
 *
 *   const kept = useKeptChoices({ ready, confirmedAt, describe });
 *
 *   - `choices` / `setChoices` work as the old useState pair (a choice per
 *     `${key}|${side}`, a typed one may carry `note`). Every change made
 *     through setChoices is saved: an option at once, a typed figure 600 ms
 *     after the last keystroke, a group's / bulk set as one request
 *     (POST /api/pricing-costing/choices). Typing never waits for the
 *     network. Each save carries the version this screen last saw for the
 *     line (`expected`) and the figure before and after (`was` / `shown`,
 *     from `describe(k)` — for the line's history).
 *   - Never overwritten silently: someone else's change since → the server
 *     refuses (`conflict`); the line takes THEIR figure and says so
 *     ("Jade Games changed it since you looked — their figure is shown").
 *   - `adopt(rows, skip)` takes what the server keeps (each row side's
 *     `kept`) when the rows load; `poll(keys, skip)` re-reads it
 *     (POST /choices/read) — every 20 seconds and on focus, the screen's
 *     call. A change made here and not yet saved wins, and lines in `skip`
 *     (rows someone is editing) are left alone; their version is NOT moved on,
 *     so a save over someone else's change is still refused.
 *   - `forgetChoices(keys)` drops choices the server has already let go of
 *     (a confirm deletes them in its own write) — nothing is sent.
 *   - `versionOf(k)` — the version last seen (a confirm carries it).
 *   - `infoFor(k)` — what to say under the figure: saving, not saved (and
 *     retrying), refused (someone else's), or who set it and when.
 *   - A save that fails is retried (3 s, 10 s, 30 s, then every minute);
 *     what is not yet saved is kept for the tab ('pricingCosting.choicesPending')
 *     and sent on leaving the screen or the page (keepalive).
 *   - Before migrations/pricing_line_choices.sql (`ready` false) nothing is
 *     sent: choices live in the tab exactly as before, and are saved once the
 *     server can keep them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import usePersistedState from './usePersistedState';
import { useNavStore } from '../store/navStore';
import { API_URL } from '../config';
import { cleanChoice, cleanNote } from '../utils/pricingCosting';

const SLOT = 'pricingCosting.choices';
const PENDING_SLOT = 'pricingCosting.choicesPending';
export const TYPED_SAVE_MS = 600;
const RETRY_MS = [3000, 10000, 30000, 60000];
const CHUNK = 500;

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
/** `${key}|${side}` → [key, side] (a line key may itself hold colons, never a bar). */
export const splitChoiceKey = (k) => {
  const i = String(k).lastIndexOf('|');
  return [String(k).slice(0, i), String(k).slice(i + 1)];
};

/** The request item for one choice as the screen holds it now. */
export function choiceItem(k, choice, { confirmedAt = null, expected = null, shown = null, was = null } = {}) {
  const [key, side] = splitChoiceKey(k);
  const clean = choice ? cleanChoice(choice) : null;
  const note = choice && choice.id === 'manual' ? (cleanNote(choice.note) || '') : '';
  return {
    key, side, choice: clean, note: note || null, confirmed_at: confirmedAt || null,
    expected: expected === undefined ? null : expected,
    ...(shown ? { shown } : {}), ...(was ? { was } : {})
  };
}

/** The screen's choice for a side the server keeps (its note on it). */
export function keptChoiceOf(kept) {
  if (!kept || !kept.choice) return undefined;
  return { ...kept.choice, ...(kept.note ? { note: kept.note } : {}) };
}

const metaOf = (kept) => (kept ? { by: kept.by || null, mine: !!kept.mine, at: kept.at || null, version: kept.version ?? null } : null);

export default function useKeptChoices({ ready = false, confirmedAt = null, describe = null } = {}) {
  const [choices, setRaw] = usePersistedState(SLOT, {});
  const choicesRef = useRef(choices);
  // What was changed here and not yet saved: key → a counter (so a save that
  // lands after a newer change does not clear it).
  const pendingRef = useRef(null);
  if (pendingRef.current === null) {
    const stored = useNavStore.getState().pages[PENDING_SLOT];
    // Before this existed every choice lived only in the tab — none was
    // ever saved, so all of them are still to send.
    const start = stored && typeof stored === 'object' && !Array.isArray(stored)
      ? stored : Object.fromEntries(Object.keys(choices || {}).map(k => [k, 1]));
    pendingRef.current = new Map(Object.entries(start));
  }
  const verRef = useRef(Math.max(1, ...[...pendingRef.current.values()].map(Number).filter(Number.isFinite)));
  const readyRef = useRef(!!ready);
  readyRef.current = !!ready;
  const confRef = useRef(confirmedAt);
  confRef.current = confirmedAt;
  const describeRef = useRef(describe);
  describeRef.current = describe;
  // The figure each pending line showed before this person changed it (history).
  const wasRef = useRef(new Map());
  const timer = useRef(null);
  const inFlight = useRef(false);
  const again = useRef(false);
  const attempt = useRef(0);
  const mounted = useRef(true);
  // Who set what, when, at which version (from the server) — a ref for the
  // saves, state for the screen — and saves in hand / failed / refused.
  const metaRef = useRef({});
  const [meta, setMetaState] = useState({});
  const [status, setStatus] = useState({});

  const updateMeta = (upd) => {
    const next = { ...metaRef.current };
    Object.entries(upd).forEach(([k, v]) => { if (v) next[k] = v; else delete next[k]; });
    metaRef.current = next;
    if (mounted.current) setMetaState(next);
  };
  const persistPending = () => useNavStore.getState().setPage(PENDING_SLOT, Object.fromEntries(pendingRef.current));
  const setLocal = (next) => {
    choicesRef.current = next;
    if (mounted.current) setRaw(next);
    else useNavStore.getState().setPage(SLOT, next);
  };

  const flushRef = useRef(null);
  const schedule = (ms) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; flushRef.current?.(); }, ms);
  };

  const flush = useCallback(async ({ keepalive = false } = {}) => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (!readyRef.current) return;
    if (inFlight.current) { again.current = true; return; }
    const keys = [...pendingRef.current.keys()];
    if (!keys.length) return;
    const sent = new Map(keys.map(k => [k, pendingRef.current.get(k)]));
    inFlight.current = true;
    if (mounted.current && !keepalive) {
      setStatus(prev => {
        const next = { ...prev };
        keys.forEach(k => { next[k] = { state: 'saving' }; });
        return next;
      });
    }
    let failedAll = null;
    const metaUpd = {};
    const statusUpd = {};
    const drop = [];
    const theirs = {};
    try {
      const conf = confRef.current;
      const desc = describeRef.current;
      for (let i = 0; i < keys.length; i += CHUNK) {
        const part = keys.slice(i, i + CHUNK);
        const items = part.map(k => choiceItem(k, choicesRef.current[k], {
          confirmedAt: conf ? conf(k) : null,
          expected: metaRef.current[k]?.version ?? null,
          shown: desc ? desc(k) : null,
          was: wasRef.current.get(k) || null
        }));
        const res = await fetch(`${API_URL}/api/pricing-costing/choices`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', keepalive,
          body: JSON.stringify({ items })
        });
        const body = await res.json().catch(() => ({}));
        if (res.status === 409 && body.code === 'CHOICES_NOT_READY') {
          // Not ready after all: kept in the tab until it is.
          readyRef.current = false;
          part.forEach(k => { statusUpd[k] = null; });
          break;
        }
        if (!res.ok || !body.success) throw new Error(body.error || `Not saved (${res.status})`);
        const results = Array.isArray(body.results) ? body.results : [];
        const byKey = new Map(results.map(r => [`${r.key}|${r.side}`, r]));
        for (const k of part) {
          const r = byKey.get(k);
          const settled = pendingRef.current.get(k) === sent.get(k);
          if (settled) { pendingRef.current.delete(k); wasRef.current.delete(k); }
          if (!r) { statusUpd[k] = null; continue; }
          if (r.outcome === 'saved') { metaUpd[k] = { by: r.by || null, mine: r.mine !== false, at: r.at || null, version: r.version ?? null }; statusUpd[k] = null; }
          else if (r.outcome === 'deleted') { metaUpd[k] = null; statusUpd[k] = null; }
          else if (r.outcome === 'skipped') { metaUpd[k] = null; statusUpd[k] = null; drop.push(k); }
          else if (r.outcome === 'conflict') {
            // Someone else's figure stands: the line takes it, and says so.
            pendingRef.current.delete(k);
            wasRef.current.delete(k);
            theirs[k] = r.current || null;
            metaUpd[k] = metaOf(r.current);
            statusUpd[k] = { state: 'conflict', message: r.message || 'Changed by someone else since you looked — yours was not saved' };
          } else statusUpd[k] = { state: 'failed', message: r.message || 'Not kept' };
        }
      }
      attempt.current = 0;
    } catch (err) {
      failedAll = err;
    } finally {
      inFlight.current = false;
    }
    persistPending();
    if (failedAll) {
      const wait = RETRY_MS[Math.min(attempt.current, RETRY_MS.length - 1)];
      attempt.current += 1;
      if (mounted.current) {
        setStatus(prev => {
          const next = { ...prev };
          keys.forEach(k => { if (pendingRef.current.has(k)) next[k] = { state: 'error', message: failedAll.message || 'Not saved', retryIn: wait }; });
          return next;
        });
        schedule(wait);
      }
      return;
    }
    if (drop.length || Object.keys(theirs).length) {
      const next = { ...choicesRef.current };
      // Confirmed since this screen looked: the confirmation stands.
      drop.forEach(k => { if (!pendingRef.current.has(k)) delete next[k]; });
      // Refused: their figure (or none) in place of ours.
      Object.entries(theirs).forEach(([k, cur]) => {
        const want = keptChoiceOf(cur);
        if (want) next[k] = want; else delete next[k];
      });
      setLocal(next);
    }
    updateMeta(metaUpd);
    if (mounted.current) {
      setStatus(prev => {
        const next = { ...prev };
        Object.entries(statusUpd).forEach(([k, v]) => { if (v) next[k] = v; else delete next[k]; });
        return next;
      });
    }
    if ((again.current || pendingRef.current.size) && readyRef.current && mounted.current) {
      again.current = false;
      // Changed again while the save was out: send the newer one.
      if ([...pendingRef.current.keys()].some(k => !sent.has(k) || sent.get(k) !== pendingRef.current.get(k))) schedule(0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  flushRef.current = flush;

  /** Change choices (as useState's setter) — each change is saved. */
  const setChoices = useCallback((upd) => {
    const prev = choicesRef.current;
    const next = typeof upd === 'function' ? upd(prev) : upd;
    if (!next || next === prev) return;
    const changed = [...new Set([...Object.keys(prev), ...Object.keys(next)])].filter(k => !same(prev[k], next[k]));
    // The figure each line showed before this burst of changes (its history's "was").
    const desc = describeRef.current;
    changed.forEach(k => {
      if (!pendingRef.current.has(k) && !wasRef.current.has(k) && desc) {
        const w = desc(k);
        if (w) wasRef.current.set(k, w);
      }
    });
    choicesRef.current = next;
    setRaw(next);
    if (!changed.length) return;
    changed.forEach(k => { verRef.current += 1; pendingRef.current.set(k, verRef.current); });
    persistPending();
    setStatus(s => {
      const out = { ...s };
      changed.forEach(k => { delete out[k]; });
      return out;
    });
    const typed = changed.some(k => next[k] && next[k].id === 'manual');
    schedule(typed ? TYPED_SAVE_MS : 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Drop choices the server has let go of already (confirmed) — nothing is sent. */
  const forgetChoices = useCallback((keys) => {
    const prev = choicesRef.current;
    let next = prev;
    (keys || []).forEach(k => {
      if (k in next) { if (next === prev) next = { ...prev }; delete next[k]; }
      pendingRef.current.delete(k);
      wasRef.current.delete(k);
    });
    persistPending();
    updateMeta(Object.fromEntries((keys || []).map(k => [k, null])));
    setStatus(s => { const out = { ...s }; (keys || []).forEach(k => { delete out[k]; }); return out; });
    if (next !== prev) setLocal(next);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Take what the server keeps for these sides: `entries` [k, kept|null].
   * Pending (not yet saved) and skipped (being edited) lines are left alone —
   * their version too.
   */
  const apply = (entries, skip) => {
    const prev = choicesRef.current;
    let next = prev;
    const metaUpd = {};
    for (const [k, kept] of entries) {
      if (pendingRef.current.has(k)) continue;
      if (skip && skip.has(k)) continue;
      metaUpd[k] = metaOf(kept);
      const want = keptChoiceOf(kept);
      if (same(prev[k], want)) continue;
      if (next === prev) next = { ...prev };
      if (want) next[k] = want; else delete next[k];
    }
    updateMeta(metaUpd);
    if (next !== prev) setLocal(next);
  };

  /** The rows' kept choices as they load (each side's `kept`). */
  const adopt = useCallback((rows, skip = null) => {
    if (!readyRef.current) return;
    const entries = [];
    for (const r of rows || []) {
      for (const side of ['cost', 'sale']) {
        const s = r && r[side];
        if (!s || !s.applies) continue;
        entries.push([`${r.key}|${side}`, s.kept || null]);
      }
    }
    apply(entries, skip);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Re-read the kept choices of these sides (`${key}|${side}`) now. Answers
   * the confirmations the server holds (`${key}|${side}` → when), or null
   * when nothing was read.
   */
  const poll = useCallback(async (sideKeys, skip = null) => {
    if (!readyRef.current || !sideKeys || !sideKeys.length) return null;
    const lineKeys = [...new Set(sideKeys.map(k => splitChoiceKey(k)[0]))];
    try {
      const res = await fetch(`${API_URL}/api/pricing-costing/choices/read`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ keys: lineKeys })
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.success || !mounted.current) return null;
      const byKey = new Map((body.choices || []).map(c => [`${c.key}|${c.side}`, c]));
      apply(sideKeys.map(k => [k, byKey.get(k) || null]), skip);
      return body.confirmations || {};
    } catch {
      return null;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** The version this screen last saw for a side (null: none). */
  const versionOf = (k) => metaRef.current[k]?.version ?? null;

  /** What to say about a side's choice: { state: 'saving'|'error'|'failed'|'conflict'|'kept'|null, … }. */
  const infoFor = (k) => {
    const st = status[k];
    const m = meta[k];
    if (st) return st.state === 'conflict' && m ? { ...m, ...st } : st;
    // Changed and waiting to go: nothing to say yet.
    if (pendingRef.current.has(k)) return null;
    return m && choicesRef.current[k] ? { state: 'kept', ...m } : null;
  };

  // Ready (the rows said so): send what was waiting.
  useEffect(() => {
    if (ready && pendingRef.current.size) schedule(0);
  }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the screen or the page: send what is not yet saved.
  useEffect(() => {
    mounted.current = true;
    const onHide = () => { if (pendingRef.current.size) flushRef.current?.({ keepalive: true }); };
    window.addEventListener('pagehide', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      mounted.current = false;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      // Signed out: the tab's slots were cleared (App.js) — nothing to send as them.
      const signedOut = useNavStore.getState().pages[PENDING_SLOT] === undefined;
      if (pendingRef.current.size && !signedOut) flushRef.current?.({ keepalive: true });
    };
  }, []);

  return { choices, setChoices, forgetChoices, adopt, poll, infoFor, versionOf, flush, pending: pendingRef };
}
